/*
 *  Product Form
 *    Try to limit the functions & methods in this class to those that
 *    are related to the primary function of a form - adding a product to the
 *    cart and allowing users to select variants via way of a visual "swatch".
 *    ProductForm can be called as it's own section or instanciated inside
 *    another section's TS.
 *
 *  Version
 *    1.0.0 - 2022/04/10
 */

import { 
  CartAdd, cartAdd, cartGetQuantityOfVariant,
  Variant, printMoney, t, VariantImage
} from '@process-creative/slate-theme-tools';
import { Swatches } from '../../objects/product/swatches';
import { ATTR_DISABLED } from "../../tools/constants/attributes";
import { EVT_VARIANT_CHANGED } from './variant-selector';
import { CLASS_INITIALISED, CLASS_DISABLED } from '../../tools/constants/classes';

import { QuantitySelectorElement } from '../quantity-selector';

export const SEL_FORM_CONTAINER = '[data-product-form]';
const SEL_ADD = '[data-product-form-add]';
const SEL_LABELS = '[data-label-element]'
const SEL_ADD_DESKTOP = '.is-desktop';
const SEL_ADD_MOBILE = '.is-mobile';
const SEL_MONEY = '[data-product-money]';
const SEL_COMPARE = '[data-product-compare-money]';
const SEL_QUANTTIY_SELECTOR = '[data-product-form-qty]';
const SEL_PRODUCT_SELLINGS = '[data-product-recharge-selling-plans]';
const SEL_RECHARGE_WIDGET = '.rc-widget';
const SELECTOR_STICKY_FORM = '[data-sticky-form]';

const STICKY_ATC = document.querySelector('[data-sticky-form] .o-sticky-atc__add-btn') as HTMLButtonElement;
const STICKY_ATC_LABEL = document.querySelector('[data-sticky-form] .o-sticky-atc__add-btn [data-label-element]');

const SELECTOR_PARENT_CAROUSEL = '[data-product-gallery-carousel]';

export class ProductForm {
  container: HTMLFormElement;
  btnAdd: HTMLButtonElement | null = null;
  btnAddMobile: HTMLElement | null = null;
  btnAddDesktop: HTMLElement | null = null;
  qtySelector: QuantitySelectorElement | null = null;
  form: HTMLFormElement | null = null;
  swatches: Swatches | null = null;
  rechargeSubscriptionOptionsElement: HTMLElement | null = null;
  rechargeWidget: HTMLElement | null = null;
  stickyPrice: HTMLElement | null = null;
  private money: NodeListOf<HTMLSpanElement> | null = null;
  private compare: NodeListOf<HTMLSpanElement> | null = null;
  private parentCarousel: HTMLElement | null = null;
  private parentCarouselTiles: NodeListOf<HTMLElement> | null = null;

  private adding = false;

  private ogPrice = 0;
  private customRetailPercentage = 0;
  private customRetailWording: String | null = null;

  constructor(container: HTMLFormElement) {
    this.container = container;
    this.queryChildren(container);
    this.attachEvents();
  }

  queryChildren(container: HTMLFormElement) {
    this.btnAdd = container.querySelector(SEL_ADD);
    this.btnAddDesktop = this.btnAdd ? this.btnAdd.querySelector(SEL_ADD_DESKTOP) : null;
    this.btnAddMobile =  this.btnAdd ? this.btnAdd.querySelector(SEL_ADD_MOBILE) : null;
    this.qtySelector = container.querySelector(SEL_QUANTTIY_SELECTOR);
    this.money = container.querySelectorAll<HTMLSpanElement>(SEL_MONEY);
    this.compare = container.querySelectorAll<HTMLSpanElement>(SEL_COMPARE);
    this.rechargeSubscriptionOptionsElement = container.querySelector(SEL_PRODUCT_SELLINGS);
    this.stickyPrice = document.querySelector(SELECTOR_STICKY_FORM);

    // this recharge widget is going to be null and only render once the app is loaded
    // should wait for the app to load before querying for this element which is about 2 seconds

    this.parentCarousel = document.querySelector(SELECTOR_PARENT_CAROUSEL)
    this.parentCarouselTiles = document.querySelectorAll('.c-product-gallery__media-container')
    setTimeout(() => {
      this.rechargeWidget = document.querySelector(SEL_RECHARGE_WIDGET);
      
    }, 2000);
  }

  // onFormSubmit = async (e: Event) => {
  //   e.preventDefault();
  //   e.stopPropagation();
    
  //   this.adding = true;
  //   this.btnPending();

  //   // Build item to add to cart then add
  // try {
  //   const item = Array.from(new FormData(this.container)).reduce((x, y) => {
  //     return { ...x, [y[0]]: y[1].toString() };
  //   }, {} as CartAdd['items'][number]);
  //   item['quantity'] = this.qtySelector ? this.qtySelector.value : 1;

  //   let rechargeSubscriptionOptions;

  //   if ('pseudo-variant' in item) {
  //     const sellingPlansWrapperEl = this.container.querySelector(`[data-pseudo-variant-selling-plans="${item.id}"]`);

  //     if (sellingPlansWrapperEl) {
  //       this.rechargeSubscriptionOptionsElement = sellingPlansWrapperEl.querySelector(SEL_PRODUCT_SELLINGS);
  //     }
  //   } else if (this.rechargeSubscriptionOptionsElement) {
  //     rechargeSubscriptionOptions = JSON.parse(this.rechargeSubscriptionOptionsElement.innerHTML);
  //     let sellingPlanGroups = rechargeSubscriptionOptions.selling_plan_groups;
  //     let sellingPlans: any[] = sellingPlanGroups.reduce((plans: any[], group: any) => {
  //       return [ ...plans, ...group.selling_plans ];
  //     }, []);

  //     item.properties = {
  //       ...item.properties,
  //       '_selling_plans': JSON.stringify(sellingPlans)
  //     };
  //   }
  //   await cartAdd({ items: [item] });
  //   this.btnAdded();
  // } catch (e) {
  //   console.error(e);
  //   alert(e);
  // }

  // this.onVariantChange();
  // if(this.qtySelector) this.qtySelector.disabled = false;
  // this.adding = false;
  // }


  onVariantChange() {
    if (!this.swatches) return;
    const variant = this.swatches.getVariant();

    // Start of gallery scroll on variant change
    const selectedVariantId = variant?.id;
    const targetMediaContainer = Array.from(this.parentCarouselTiles!).find(container => 
      container.getAttribute('data-variant') === selectedVariantId.toString()
    );
    targetMediaContainer?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    // End of gallery scroll
    
    if (variant === undefined) return;

    const {
      inventory_management, inventory_policy, inventory_quantity,
      price, compare_at_price, selling_plan_allocations
    } = variant;
    const inCart = cartGetQuantityOfVariant(variant.id);

    if(this.qtySelector) {
      if(inventory_management === 'shopify' && inventory_policy === 'deny') {
        this.qtySelector.max = (inventory_quantity || 0) - inCart;
      } else {
        this.qtySelector.max = null;
      }
    }

    if (this.container.classList.contains('c-product-main__form')) {
      this.updatePriceElements(price, compare_at_price);
      this.updateAddToCart(variant);
      this.selectFeaturedImage();
      this.updateRechargeVariant(selling_plan_allocations, price, compare_at_price);
    }
  }

  // Select Featured Image
  selectFeaturedImage() {
    if (!this.swatches) return;
    const variant = this.swatches.getVariant();
    const featuredImageObj = variant.featured_media as VariantImage;
    const imageId = featuredImageObj?.id;
    document.dispatchEvent(new CustomEvent('select_featured_image', { detail: imageId, bubbles: true }));
  }

  /**
   * Updates elements displaying price based on selected variant
   * @param price variant price
   * @param comparePrice variant compare at price
   */

  updateRechargeVariant(selling_plan_allocations: any, price: any, compare_at_price: any) {
    // Set Correct Recharge
    const rechargeBlock = this.container.querySelector('.recharge-subscription-widget');
    const rcSellingPlan = rechargeBlock?.querySelector('.subscription-radio') as HTMLLabelElement;
    const oneTime = rechargeBlock?.querySelector('.onetime-radio') as HTMLLabelElement;
    const spinput = this.container.querySelector<HTMLInputElement>('[name="selling_plan"]')
    const stickyFormPrice = this.stickyPrice?.querySelector('.o-sticky-atc__price') as HTMLSpanElement;
    const formPrice = this.container.querySelector<HTMLElement>('.c-product-form__price');
    const sellingPlanSelect = this.container.querySelector<HTMLSelectElement>('.rc-selling-plans-dropdown__select');
    
    if (selling_plan_allocations.length === 0) {
      this.container?.classList.remove('has-selling-plan');
      setTimeout(() => {
        if(spinput) spinput.value = '';

        if (formPrice) formPrice.innerHTML = `${printMoney(price)}`;
        if (stickyFormPrice) stickyFormPrice.innerHTML = `${printMoney(price)}`

      }, 1000);
    } else {
      rcSellingPlan.click();
      if(spinput) spinput.value = `${sellingPlanSelect?.value}`;
      this.container?.classList.add('has-selling-plan');

      this.updateStickyPriceBasedOnSubscription();
    }
  }

  updatePriceElements(price: number, comparePrice?: number | null) {
    if(this.money) {
      this.money.forEach(el => el.innerText = printMoney(price));
    }

    if(this.compare) {
      if(comparePrice) {
        this.compare.forEach(el => el.innerText = printMoney(comparePrice));
      } else {
        this.compare.forEach(el => el.innerText = '');
      }
    }
    // Find the element with subscription-radio class and check if it has the class rc-radio--active
    const subscriptionRadio = document.querySelector('.subscription-radio') as HTMLInputElement;
    const oneTimeRadio = document.querySelector('.onetime-radio') as HTMLInputElement;
    const priceSpan = this.stickyPrice?.querySelector('.o-sticky-atc__price') as HTMLSpanElement;
    const priceHtml = this.container?.querySelector('.c-product-form__price') as HTMLSpanElement;

    if (subscriptionRadio && subscriptionRadio.classList.contains('rc-radio--active')) {
      // Grab the value of the subscription price after 0.2 seconds since it is still grabbing the initial value
      setTimeout(() => {
        const subscriptionPrice = subscriptionRadio.querySelector('.rc-radio__label .rc-radio__price') as HTMLSpanElement;
        const oneTimePrice = oneTimeRadio.querySelector('.rc-radio__label') as HTMLSpanElement;

        let subscriptionPriceDiscount = Number(price);
        subscriptionPriceDiscount *= 0.9;

        if (oneTimePrice) {
          oneTimePrice.innerText = `One-time purchase ${printMoney(Number(price))}`;
        }

        if (subscriptionPrice) {
          subscriptionPrice.innerText = `${printMoney(Number(subscriptionPriceDiscount))}`
        }

        if (priceSpan) {
          this.updatePriceValue(priceSpan, printMoney(Number(subscriptionPriceDiscount)));
        }
        
        if (priceHtml) {
          this.updatePriceValue(priceHtml, printMoney(Number(subscriptionPriceDiscount)));
        }
      }, 200);
    } else {
      if (priceSpan) {
        this.updatePriceValue(priceSpan, printMoney(price));
      }
      if (priceHtml) {
        this.updatePriceValue(priceHtml, printMoney(price));
      }
    }
  }

  /**
   * Updates the sticky add to cart button price based on the recharge widget
   */
  updateStickyPriceBasedOnSubscription() {
    if (!this.rechargeWidget) return;
    const radios = this.rechargeWidget.querySelectorAll<HTMLInputElement>('input[type="radio"]');
    radios.forEach(radio => {
      radio.addEventListener('change', () => {
        this.updateStickyPriceElement(radio);
      });
      if(radio.checked) this.updateStickyPriceElement(radio);
    })
  }

  updateStickyPriceElement(radio: HTMLInputElement) {
    const priceSpan = this.stickyPrice?.querySelector('.o-sticky-atc__price') as HTMLSpanElement;
    const priceHtml = this.container?.querySelector('.c-product-form__price') as HTMLSpanElement;

    if (radio.value === 'subscription') {
      document.querySelector('[data-product-main]')?.classList.remove('is-one-time');
      const subscriptionPrice = radio.nextElementSibling?.querySelector('.rc-radio__label .rc-radio__price') as HTMLSpanElement;
      let subscriptionPriceValue = subscriptionPrice?.innerText;

      if (radio.nextElementSibling?.querySelector('.rc-radio__label .rc-radio__price .is-price')) {
        subscriptionPriceValue = `${printMoney(Number(this.ogPrice))}`
      }

      this.updatePriceValue(priceSpan, subscriptionPriceValue);
      this.updatePriceValue(priceHtml, subscriptionPriceValue);
    } else if (radio.value === 'onetime') {
      document.querySelector('[data-product-main]')?.classList.add('is-one-time');
      const oneTimePrice = document.querySelector('[data-product-money]') as HTMLSpanElement;
      const oneTimePriceValue = oneTimePrice?.innerText;
      this.updatePriceValue(priceSpan, oneTimePriceValue);
      this.updatePriceValue(priceHtml, oneTimePriceValue);
    }
  }

  updatePriceValue(priceSpan: HTMLSpanElement, priceValue: string | undefined) {
    setTimeout(() => {
      if (priceSpan) {
        priceSpan.innerText = priceValue || '';
      }
    }, 200);
  }

  /**
   * Updates Add to cart button label based on selected variant
   * @param variant - variant object
   */
  updateAddToCart(variant: Variant) {
    
    if (!this.btnAdd) return;

    // Adjust the add to cart button based on QTY available and in cart
    const inCart = cartGetQuantityOfVariant(variant.id);
    const {
      available,
      inventory_policy,
      inventory_management,
      inventory_quantity,
    } = variant;

    if (available) {
      if (
        inventory_policy != 'continue' &&
        inventory_management != null &&
        ((inventory_quantity || 0) - inCart) <= 0
      ) {
        this.btnAllInCart();
      } else {
        this.btnAvailable();
      }
    } else {
      this.btnSoldOut();
    }
  }
  

  /** 
   * Enables button and updates label
   */
  btnAvailable() {
    if(!this.btnAdd) return;
    
    if (document.body.classList.contains('c-page--product')) {
      this.btnAdd.querySelectorAll(SEL_LABELS).forEach( e => e.innerHTML = t('products.product.add_to_cart_pdp'));
      if(STICKY_ATC_LABEL) STICKY_ATC_LABEL.innerHTML = t('products.product.add_to_cart_pdp');
     } else {
      if(!this.btnAddDesktop || !this.btnAddMobile) return;
      this.btnAddDesktop.querySelectorAll(SEL_LABELS).forEach(e => e.innerHTML = t('products.product.quick_add'));
      if(STICKY_ATC_LABEL) STICKY_ATC_LABEL.innerHTML = t('products.product.quick_add');
      this.btnAddMobile.querySelectorAll(SEL_LABELS).forEach(e => e.innerHTML = t('products.product.add_to_cart') );
    }

    

    this.btnAdd.classList.remove(CLASS_DISABLED);
    this.btnAdd.removeAttribute(ATTR_DISABLED);
    this.btnAdd.removeAttribute('disabled');
    this.btnAdd.disabled = false;

    if(STICKY_ATC) {
      STICKY_ATC.classList.remove(CLASS_DISABLED);
      STICKY_ATC.removeAttribute(ATTR_DISABLED);
      STICKY_ATC.removeAttribute('disabled');
      STICKY_ATC.disabled = false;
    }
  }

  /**
   * Updates button label if all items are in the cart
   */
  btnAllInCart() {
    if (!this.btnAdd) return;
    this.btnAdd.classList.add(CLASS_DISABLED);
    this.btnAdd.setAttribute(ATTR_DISABLED, ATTR_DISABLED);
    this.btnAdd.setAttribute('disabled', '');
    this.btnAdd.querySelectorAll(SEL_LABELS).forEach( e => e.innerHTML = t('products.product.all_in_cart'));
    if(STICKY_ATC_LABEL) STICKY_ATC_LABEL.innerHTML = t('products.product.all_in_cart');

    if(STICKY_ATC) {
      STICKY_ATC.classList.add(CLASS_DISABLED);
      STICKY_ATC.setAttribute(ATTR_DISABLED, ATTR_DISABLED);
      STICKY_ATC.setAttribute('disabled', '');
    }
  }

  /** 
   * Disables ATC if out of stock
   */
  btnSoldOut() {
    if (!this.btnAdd) return;
    this.btnAdd.classList.add(CLASS_DISABLED);
    this.btnAdd.setAttribute(ATTR_DISABLED, ATTR_DISABLED);
    this.btnAdd.setAttribute('disabled', '');
    this.btnAdd.querySelectorAll(SEL_LABELS).forEach( e => {e.innerHTML = t('products.product.sold_out') });
    if(STICKY_ATC_LABEL) STICKY_ATC_LABEL.innerHTML = t('products.product.sold_out');

    if(STICKY_ATC) {
      STICKY_ATC.classList.add(CLASS_DISABLED);
      STICKY_ATC.setAttribute(ATTR_DISABLED, ATTR_DISABLED);
      STICKY_ATC.setAttribute('disabled', '');
    }
  }

  /** 
   * Shows a success state after add to cart
   */
  btnAdded() {
    if (!this.btnAdd) return;
    this.btnAdd.querySelectorAll(SEL_LABELS).forEach( e => e.innerHTML = t('products.product.added_to_cart'));
    if(STICKY_ATC_LABEL) STICKY_ATC_LABEL.innerHTML = t('products.product.added_to_cart');
    this.btnAdd.classList.remove(CLASS_DISABLED);
    this.btnAdd.removeAttribute(ATTR_DISABLED);
    this.btnAdd.removeAttribute('disabled');
    this.btnAdd.disabled = false;
    if(STICKY_ATC) {
      STICKY_ATC.classList.remove(CLASS_DISABLED);
      STICKY_ATC.removeAttribute(ATTR_DISABLED);
      STICKY_ATC.removeAttribute('disabled');
      STICKY_ATC.disabled = false;
    }
  }

  /** 
   * Updates btn to pending state
   */
  btnPending() {
    if(!this.btnAdd) return;
    this.btnAdd.querySelectorAll(SEL_LABELS).forEach( e => e.innerHTML = t('products.product.cart_pending'));
    if(STICKY_ATC_LABEL) STICKY_ATC_LABEL.innerHTML = t('products.product.cart_pending');
    this.btnAdd.disabled = true;
    if(STICKY_ATC) {
      STICKY_ATC.disabled = true;
    }
  }

  initSwatches() {
    this.swatches = new Swatches(this.container, true);

    // Select Initial Featured Variant Image
    setTimeout(() => {
      this.selectFeaturedImage();
    }, 1000);
  }

  /** 
   * We are removing the content of the Injected code from Recharge because the user
   * shouldn't been able to access the prices when not logged in
   */
  removeInjectedSubscription() {
    const parentNode = document.body;

    if (!parentNode) {
      console.error('Parent node not found');
      return;
    }

    const config = { attributes: true, childList: true, subtree: true };

    const callback = (mutationList: MutationRecord[], observer: MutationObserver) => {
      for (const mutation of mutationList) {
        if (mutation.type === "childList") {
          const widgetContainer = document.querySelector('[data-widget-container-wrapper]');
          if (widgetContainer) {
            widgetContainer.innerHTML = '';
            observer.disconnect();
            break;
          }
        } else if (mutation.type === "attributes") {
          console.log(`The ${mutation.attributeName} attribute was modified.`);
        }
      }
    };

    const observer = new MutationObserver(callback);
    observer.observe(parentNode, config);
  }

  attachEvents() {
    this.initSwatches();
    this.container.addEventListener(EVT_VARIANT_CHANGED, () => this.onVariantChange());
    // this.container.addEventListener('submit', this.onFormSubmit);
    // this.btnAdd?.addEventListener('click', this.onFormSubmit);

    // this.removeInjectedSubscription();

    //Update button state on page load
    // this.updateAddToCart(this.swatches.getVariant());

    // also have to add a set timeout here to wait for the recharge widget to load
    setTimeout(() => {
      this.updateStickyPriceBasedOnSubscription();
      this.updateRechargeCustomPercentage();
    }, 2000);

    // sticky ATC should just click regular ATC
    if(STICKY_ATC) {
      STICKY_ATC.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        let productMainButton = document.querySelector('[data-product-main] [data-product-form-add]') as HTMLButtonElement;
        if (productMainButton) productMainButton.click();
      });
    }
  }

  updateRechargeCustomPercentage() {
    const productContainer = document.querySelector('[data-product-main]') as HTMLElement | null;
    
    if (!productContainer) return;

    const rechargeSubcription = productContainer.querySelectorAll('.rc-radio__subscription');
    const formSubscriptionOptions = productContainer.querySelectorAll('.rc-radio')
    const productUnitQty = Number(productContainer.dataset.productUnitQty || 0)
    const productCompareAtPrice = Number(productContainer.dataset.productComparePrice || 0)
    const productOgPrice = Number(productContainer.dataset.productOgPrice || 0)
    
    const radioPrices = document?.querySelectorAll('.rc-radio__price');
    
    this.customRetailPercentage = Number(productContainer?.getAttribute('data-custom-percentage'));
    this.customRetailWording = String(productContainer?.getAttribute('data-custom-wording'));

    rechargeSubcription?.forEach(i => {
      if (this.customRetailPercentage && this.customRetailPercentage > 0) {
        i.innerHTML = `${t('products.product.custom_subscribe_save')} ${this.customRetailPercentage}% ${this.customRetailWording}`
      }
    })

    if (this.customRetailPercentage && this.customRetailPercentage > 0) {
      
      radioPrices?.forEach(radioPrice => {

        this.ogPrice = Number(String(radioPrice.textContent).replace('$', '').replace('.', ''))
        const customRetailPercentageDivided = this.customRetailPercentage / 100;
        const divisor = 1 - customRetailPercentageDivided;
        const comparePrice = this.ogPrice / divisor;

        radioPrice.innerHTML = `
          <span class="is-price">${this.removePriceSpace(printMoney(Number(this.ogPrice)))}</span>
          <span class="is-compare">${this.removePriceSpace(printMoney(Number(comparePrice)))}</span>
        `
      })
    }

    if (productUnitQty > 0) {
      formSubscriptionOptions?.forEach(element => {
        const optionLabel = element.querySelector('.rc-radio__label');

        if (!optionLabel) return;

        let additionalInfo = optionLabel.querySelector('.rc-radio__additional-info') as HTMLElement | null;
        if (!additionalInfo) {
          additionalInfo = document.createElement('span');
          additionalInfo.classList.add('rc-radio__additional-info');
        }

        if (element.classList.contains('subscription-radio')) {
          const priceElement = optionLabel.querySelector('.rc-radio__price');
          const rawPrice = priceElement?.textContent?.replace('$', '').replace(',', '').trim() || '0';
          const formattedPrice = parseFloat(rawPrice) || 0;
          const unitCost = formattedPrice / productUnitQty;

          const subscriptionElement = optionLabel.querySelector('.rc-radio__subscription');
          if (subscriptionElement) {
            const priceUnit = `${printMoney(unitCost * 100)}/${t('products.product.unit')}`
            additionalInfo.textContent = this.removePriceSpace(priceUnit);
            subscriptionElement.appendChild(additionalInfo);
          }
        } else if (element.classList.contains('onetime-radio')) {
          // assuming the DOM hasn't manipulated yet, remove the price in the label
          if (optionLabel && optionLabel.textContent) {
            const priceMatch = optionLabel.textContent.match(/\$\s*\d+(\.\d{2})?/);
            const priceText = priceMatch ? priceMatch[0] : '';

            optionLabel.textContent = optionLabel.textContent.replace(priceText, '').trim();
          }

          if (!optionLabel.querySelector('.rc-radio__label-wrapper')) {
            const wrapper = document.createElement('span');
            wrapper.classList.add('rc-radio__label-wrapper');
            
            while (optionLabel.firstChild) {
              wrapper.appendChild(optionLabel.firstChild);
            }

            optionLabel.appendChild(wrapper);
          }

          const priceSpan = document.createElement('span');
          priceSpan.classList.add('rc-radio__price');
          priceSpan.textContent = this.removePriceSpace(printMoney(productOgPrice));
          optionLabel.appendChild(priceSpan);

          const ogPriceWithUnit = `${printMoney(productOgPrice / productUnitQty)}/${t('products.product.unit')}`
          additionalInfo.textContent = this.removePriceSpace(ogPriceWithUnit);
          optionLabel.querySelector('.rc-radio__label-wrapper')?.appendChild(additionalInfo);
        }
      });
    }
  }

  removePriceSpace(price: string) {
    return price.replace(/\s+/g, '')
  }

  reinitialiseSwatches(productData) {
    this.detatchEvents();
    this.swatches?.loadData(productData);
    this.attachEvents();
    this.onVariantChange();
  }

  detatchEvents() {
    this.swatches?.dispose();
    // this.container.removeEventListener('submit', this.onFormSubmit);
    // this.container.removeEventListener(EVT_VARIANT_CHANGED, () => this.onVariantChange());
  }
}
